import * as React from 'react'
import { Table, Tag, Typography, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { humanTransactionRequestStatus } from '../../const'

const { Text } = Typography

type ChangeHistoryEntry = {
  id: number | string
  entityType?: string
  entityId?: number
  actionType: string
  actionDetails?: string
  createdAt: string
  user?: {
    email: string
    fullName: string
  }
}

type ActionHistoryProps = {
  history: ChangeHistoryEntry[]
  createdAt?: string
  acceptedAt?: string
}

const formatActionDetails = (actionDetails: string): React.ReactNode => {
  try {
    const details = JSON.parse(actionDetails)
    if (!details || Object.keys(details).length === 0) return '—'

    return Object.entries(details).map(([key, value]) => {
      let displayValue = String(value)
      if (key === 'status') {
        displayValue = humanTransactionRequestStatus(displayValue) || displayValue
      }
      return (
        <div key={key}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {key}:
          </Text>{' '}
          <Text style={{ fontSize: 12 }}>{displayValue}</Text>
        </div>
      )
    })
  } catch {
    return actionDetails || '—'
  }
}

const actionTypeColor = (type: string): string => {
  switch (type) {
    case 'created':
      return 'cyan'
    case 'accepted':
      return 'green'
    case 'status_changed':
      return 'blue'
    case 'update':
      return 'default'
    default:
      return 'default'
  }
}

const actionTypeLabel = (type: string): string => {
  switch (type) {
    case 'created':
      return 'Created'
    case 'accepted':
      return 'Accepted'
    case 'status_changed':
      return 'Status changed'
    case 'update':
      return 'Updated'
    default:
      return type || 'Updated'
  }
}

const columns: ColumnsType<ChangeHistoryEntry> = [
  {
    title: 'Date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (v: string) =>
      v ? (
        <Tooltip title={v}>
          <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {dayjs(v).format('DD MMM YYYY HH:mm:ss')}
          </Text>
        </Tooltip>
      ) : (
        '—'
      ),
  },
  {
    title: 'User',
    key: 'user',
    width: 200,
    render: (_: any, record: ChangeHistoryEntry) => (
      <div>
        <Text strong style={{ fontSize: 12 }}>
          {record.user?.fullName || '—'}
        </Text>
        {record.user?.email && (
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.user.email}
            </Text>
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Action',
    dataIndex: 'actionType',
    key: 'actionType',
    width: 130,
    render: (v: string) => (
      <Tag color={actionTypeColor(v)} style={{ fontSize: 11 }}>
        {actionTypeLabel(v)}
      </Tag>
    ),
  },
  {
    title: 'Details',
    dataIndex: 'actionDetails',
    key: 'actionDetails',
    render: (v: string) => formatActionDetails(v),
  },
]

export const ActionHistory: React.FC<ActionHistoryProps> = ({
  history,
  createdAt,
  acceptedAt,
}) => {
  const entries: ChangeHistoryEntry[] = [...(history || [])]

  if (createdAt) {
    entries.push({ id: 'created', actionType: 'created', createdAt })
  }
  if (acceptedAt) {
    entries.push({ id: 'accepted', actionType: 'accepted', createdAt: acceptedAt })
  }

  const sorted = entries.sort(
    (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
  )

  return (
    <Table
      columns={columns}
      dataSource={sorted}
      rowKey="id"
      size="small"
      pagination={false}
      locale={{ emptyText: 'No action history yet' }}
    />
  )
}
